/**
 * Loose `t()` wrapper for dynamic-key lookups in production code.
 *
 * The typed-options ParseKeys union from `i18next` + `react-i18next`
 * grew past TypeScript's complexity threshold (TS2590) once both
 * mobile + web key trees co-exist in shared/i18n/locales/en.json. Most
 * call sites continue to use the typed `t()` from `useTranslation()`:
 *   - Literal-string keys (`t('web.home.title')`) work fine.
 *   - `as const` literal-string tuples on map declarations
 *     (`SEVERITY_LABEL_KEYS = { ... } as const satisfies Record<...>`)
 *     also resolve through the typed union.
 *
 * Where this wrapper IS needed:
 *   - `Object.entries(KEY_MAP).map(([slug, labelKey]) => t(labelKey))`
 *     — `Object.entries` strips the literal-string types down to plain
 *     `string`, so the typed `t()` rejects the loop variable.
 *   - Dynamic lookups with a `?? ''` fallback widen the union past
 *     ParseKeys.
 *
 * Typo safety on every literal-string key passed to `t()` / `tLoose()`
 * is enforced statically by
 * `shared/i18n/scripts/lint-call-sites.ts` — CI fails before a typo
 * can ship. Use this wrapper at the narrowest scope: prefer the typed
 * `t()` everywhere else.
 */
import { useTranslation } from 'react-i18next';

export function useTLoose(): (key: string, options?: Record<string, unknown>) => string {
  const { t } = useTranslation();
  return (key, options) => t(key as never, options as never) as unknown as string;
}
